import logger from './logger';
import { toJs } from './toJs';

export class IndexDB {
  blackList = ['/ticket/', '/batchTicket/', '/log/', '/externalOrder/'];
  whiteList = ['/ticket/template', '/static/'];
  // These are legacy keys that should be
  // consistently cleaned up after a few releases
  legacyKeyMappings = {
    // 'new key': 'old key',
    'mixDesignsWithTrim': 'mixDesignsTrim',
  };

  constructor(dbName) {
    this.dbName = dbName || 'CloudBatchDB';
    this.objectStoreName = 'CBData';
    this.db = null;
  }

  init() {
    if (!('indexedDB' in window)) {
      return Promise.reject('IndexedDB is not available');
    }

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = (event) => {
        this.db = event.target.result;

        if (!this.db.objectStoreNames.contains(this.objectStoreName)) {
          this.db.createObjectStore(this.objectStoreName, { keyPath: 'key' });
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }

  shouldSaveItem(key) {
    let shouldSave = true;

    this.blackList.forEach(v => {
      if (key.includes(v)) {
        shouldSave = false;
      }
    });

    this.whiteList.forEach(v => {
      if (key.includes(v)) {
        shouldSave = true;
      }
    });

    return shouldSave;
  }

  save(key, data) {
    if (!('indexedDB' in window)) {
      return Promise.reject('IndexedDB is not available');
    }
    if (!this.db) {
      return Promise.reject('IndexDB is not initialized');
    }

    if (!this.shouldSaveItem(key)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.objectStoreName], 'readwrite');
      const objectStore = transaction.objectStore(this.objectStoreName);
      const request = objectStore.put({
        key: key,
        value: toJs(data),
        timestamp: Date.now(), // Store timestamp for TTL checking
      });

      request.onsuccess = (_) => {
        resolve();
      };

      request.onerror = (event) => {
        logger.warn(`Issue saving ${key} to indexDB `, data, event.target.error);
        reject(event.target.error);
      };
    });
  }

  async getByKey(key, tryLegacy = true) {
    if (!('indexedDB' in window) || !this.db) {
      logger.error('IndexedDB is not available');
      throw new Error('IndexedDB is not available');
    }

    try {
      const data = await this._getByKey(key);
      if (data !== null) {
        return data;
      }
    } catch (error) {
      logger.warn(`Error retrieving data for key ${key}:`, error);
    }

    if (tryLegacy && this.legacyKeyMappings[key]) {
      const legacyKey = this.legacyKeyMappings[key];
      logger.log(`Primary key ${key} not found, loading legacy key ${legacyKey}`);

      try {
        return await this._getByKey(legacyKey);
      } catch (error) {
        logger.warn(`Error retrieving data for legacy key ${legacyKey}:`, error);
      }
    }

    return null;
  }

  /**
   * @private
   */
  _getByKey(key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.objectStoreName], 'readonly');
      const objectStore = transaction.objectStore(this.objectStoreName);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const result = event.target.result;
        if (result) {
          resolve(result.value);
        } else {
          resolve(null);
        }
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }

  /**
   * Get cached data if it exists and hasn't expired
   * @param {string} key - Cache key
   * @param {number} ttl - Time to live in milliseconds (e.g., 30 * 60 * 1000 for 30 minutes)
   * @returns {Promise<any|null>} Cached data if valid, null if expired or not found
   */
  async getByKeyWithTTL(key, ttl) {
    if (!('indexedDB' in window) || !this.db) {
      logger.error('IndexedDB is not available');
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.objectStoreName], 'readonly');
      const objectStore = transaction.objectStore(this.objectStoreName);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const result = event.target.result;

        if (!result) {
          logger.debug(`Cache MISS: ${key}`);
          resolve(null);
          return;
        }

        // Check if timestamp exists (backward compatibility)
        if (!result.timestamp) {
          logger.debug(`Cache HIT (no timestamp): ${key}`);
          resolve(result.value);
          return;
        }

        // Check if cache has expired
        const age = Date.now() - result.timestamp;
        if (age > ttl) {
          logger.debug(`Cache EXPIRED: ${key} (age: ${age}ms, ttl: ${ttl}ms)`);
          resolve(null);
          return;
        }

        logger.debug(`Cache HIT: ${key} (age: ${age}ms, ttl: ${ttl}ms)`);
        resolve(result.value);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }

  removeByKey(key) {
    if (!('indexedDB' in window) || !this.db) {
      return Promise.reject('IndexedDB is not available');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.objectStoreName], 'readwrite');
      const objectStore = transaction.objectStore(this.objectStoreName);

      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        resolve();
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }

  clear() {
    if (!('indexedDB' in window) || !this.db) {
      return Promise.reject('IndexedDB is not available');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.objectStoreName], 'readwrite');
      const objectStore = transaction.objectStore(this.objectStoreName);

      const request = objectStore.clear();

      request.onsuccess = (event) => {
        logger.log('IndexDB cleared successfully');
        resolve();
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }
}
